import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuTicket, LuZap, LuCopy, LuExternalLink } from 'react-icons/lu';

import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Badge from '../../components/ui/Badge/Badge';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import CurrencyAmount from '../../components/ui/CurrencyAmount/CurrencyAmount';
import { formatSessionDate } from '../../utils/formatDate';
import { listMyRedeemCodes } from '../../api/redeemCodesApi';
import { useToast } from '../../context/ToastContext';
import { copyToClipboard } from '../../utils/clipboard';
import './RedeemCodesPage.less';

/**
 * Authenticated redeem-codes page — `/redeem-codes`.
 *
 * Data source is `GET /api/redeem-codes/mine` (authRequired,
 * `user_id = req.user.id`). Codes are pre-owned at issuance — a
 * customer never "claims" a code, so this page is intentionally
 * read-only. Redemption happens on the charging screen via the
 * existing session-start flow; the actions below just take the
 * customer TO that flow — they never call redeem endpoints
 * directly.
 */

const STATUS_META = {
  active:    { label: 'Available', tone: 'success' },
  redeemed:  { label: 'Redeemed',  tone: 'info' },
  expired:   { label: 'Expired',   tone: 'warning' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

const SECTION_ORDER = ['active', 'redeemed', 'expired', 'cancelled'];

// Route helper — a single source of truth for where the actions
// send the user. Consumers should never hardcode these paths in
// their onClick handlers.
const ROUTES = {
  charge: '/charge',
  sessionDetail: (uid) => `/history/${encodeURIComponent(uid)}`,
};

function groupByStatus(codes) {
  const out = { active: [], redeemed: [], expired: [], cancelled: [] };
  for (const c of codes) {
    const key = out[c.status] ? c.status : null;
    if (key) out[key].push(c);
  }
  return out;
}

function CodeCard({ code, onCopy, onUseNow, onViewSession }) {
  const meta = STATUS_META[code.status] || STATUS_META.active;

  const dateLabel =
    code.status === 'redeemed' && code.redeemedAt
      ? `Redeemed ${formatSessionDate(code.redeemedAt)}`
      : code.status === 'cancelled' && code.cancelledAt
      ? `Cancelled ${formatSessionDate(code.cancelledAt)}`
      : code.status === 'expired' && code.expiresAt
      ? `Expired ${formatSessionDate(code.expiresAt)}`
      : code.status === 'active' && code.expiresAt
      ? `Expires ${formatSessionDate(code.expiresAt)}`
      : `Issued ${formatSessionDate(code.createdAt)}`;

  const isActive = code.status === 'active';
  const isRedeemed = code.status === 'redeemed';
  const showViewSession = isRedeemed && !!code.redeemedSessionId;

  return (
    <Card className="gc-redeem-row">
      <div className="gc-redeem-row-main">
        <div className="gc-redeem-row-heading">
          <h3 className="gc-redeem-row-code">{code.code}</h3>
          <Badge tone={meta.tone} className="gc-redeem-row-badge">
            {meta.label}
          </Badge>
        </div>
        <p className="gc-redeem-row-when">{dateLabel}</p>
        <p className="gc-redeem-row-amount">
          <CurrencyAmount
            value={Number(code.amount)}
            currency={code.currency || 'BBD'}
          />
        </p>
      </div>

      {(isActive || showViewSession) && (
        <>
          <hr className="gc-redeem-row-divider" aria-hidden="true" />
          <div className="gc-redeem-row-actions">
            {isActive && (
              <>
                <Button
                  type="button"
                  variant="primary"
                  leadingIcon={<LuCopy />}
                  onClick={() => onCopy(code)}
                  aria-label={`Copy redeem code ${code.code} to clipboard`}
                >
                  Copy code
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  leadingIcon={<LuZap />}
                  onClick={() => onUseNow(code)}
                  aria-label={`Use redeem code ${code.code} now — go to Find Charger`}
                >
                  Use now
                </Button>
              </>
            )}
            {showViewSession && (
              <Button
                type="button"
                variant="secondary"
                leadingIcon={<LuExternalLink />}
                onClick={() => onViewSession(code)}
                aria-label={`View the session where ${code.code} was redeemed`}
              >
                View session
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function Section({ statusKey, codes, onCopy, onUseNow, onViewSession }) {
  if (!codes.length) return null;
  const meta = STATUS_META[statusKey];
  return (
    <section
      className="gc-redeem-section"
      aria-labelledby={`gc-redeem-h-${statusKey}`}
    >
      <h2 id={`gc-redeem-h-${statusKey}`} className="gc-redeem-section-title">
        {meta.label}
        <span className="gc-redeem-section-count">{codes.length}</span>
      </h2>
      <div className="gc-redeem-section-list">
        {codes.map((c) => (
          <CodeCard
            key={c.id}
            code={c}
            onCopy={onCopy}
            onUseNow={onUseNow}
            onViewSession={onViewSession}
          />
        ))}
      </div>
    </section>
  );
}

function RedeemCodesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const result = await listMyRedeemCodes();
    if (result.ok) {
      setCodes(result.codes);
    } else {
      setError({
        code: result.error,
        message: result.message,
        status: result.status,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Actions ─────────────────────────────────────────────────────

  const handleCopy = useCallback(
    async (code) => {
      // `copyToClipboard` is documented as never-throws — but the
      // handler wraps in try/catch anyway so a future refactor that
      // regresses that guarantee can't bubble an unhandled rejection
      // into React or leak an error message to the user.
      let ok = false;
      try {
        ok = await copyToClipboard(code.code);
      } catch {
        ok = false;
      }
      if (ok) {
        showToast({ message: 'Redeem code copied.', type: 'success' });
      } else {
        // Never leak the underlying reason to the user. A single
        // friendly line + the code inline so they can still copy
        // manually.
        showToast({
          message: `Couldn't copy automatically. Code: ${code.code}`,
          type: 'info',
        });
      }
    },
    [showToast],
  );

  const handleUseNow = useCallback(() => {
    // Do NOT auto-redeem. The customer enters the code on the
    // charging screen exactly as they would if they'd typed it —
    // this button is a shortcut, not an apply.
    navigate(ROUTES.charge);
  }, [navigate]);

  const handleViewSession = useCallback(
    (code) => {
      if (!code.redeemedSessionId) return;
      navigate(ROUTES.sessionDetail(code.redeemedSessionId));
    },
    [navigate],
  );

  const grouped = groupByStatus(codes);
  const hasAny = codes.length > 0;
  const showInitialSkeleton = loading && !hasAny && !error;

  return (
    <Page>
      <PageHeader
        title="Redeem Codes"
        subtitle="View and manage your GreenCharge redeem codes."
      />

      {showInitialSkeleton && (
        <div className="gc-redeem-list" aria-busy="true" aria-live="polite">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {!showInitialSkeleton && error && (
        <Card>
          <ErrorState
            title="We couldn't load your redeem codes"
            description={error.message || 'Please check your connection and try again.'}
            onRetry={load}
          />
        </Card>
      )}

      {!showInitialSkeleton && !error && !hasAny && (
        <Card>
          <EmptyState
            icon={<LuTicket />}
            title="No redeem codes yet"
            description={
              'Redeem codes are issued by GreenCharge support as a credit ' +
              'against a future charging session. When you have one, it will ' +
              'appear here.'
            }
            action={
              <Button as={Link} to={ROUTES.charge} leadingIcon={<LuZap />}>
                Find a charger
              </Button>
            }
          />
        </Card>
      )}

      {!showInitialSkeleton && !error && hasAny && (
        <div className="gc-redeem-sections">
          {SECTION_ORDER.map((k) => (
            <Section
              key={k}
              statusKey={k}
              codes={grouped[k]}
              onCopy={handleCopy}
              onUseNow={handleUseNow}
              onViewSession={handleViewSession}
            />
          ))}
        </div>
      )}
    </Page>
  );
}

export default RedeemCodesPage;
